import { number, t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { CircleHelpIcon, FilesIcon, LoaderCircleIcon, PlusIcon, UsersIcon } from 'lucide-react';
import { useState } from 'react';
import { MonthlyChart } from '@/components/raven/charts';
import { dateLabel, OLink, QueryState } from '@/components/raven/shared';
import { useSession } from '@/providers/session';
import { trpc } from '@/providers/trpc';

export const OrganizationIdPage = () => {
  useLocale();

  const { user } = useSession();
  const [reportType, setReportType] = useState<'KYC' | 'KYB' | ''>('');
  const [days, setDays] = useState(180);
  const query = trpc.workspace.getDashboard.useQuery(
    { reportType: reportType || undefined, days },
    { refetchInterval: 30000 },
  );
  const data = query.data;
  const filter = `reportType=${reportType || 'ALL'}&days=${days}`;
  const metrics = [
    {
      get label() {
        return t('auth.totalReports');
      },
      count: data?.totalReports,
      get unit() {
        return t('workspace.reports');
      },
      Icon: FilesIcon,
      detail:
        data?.reportChange === null
          ? t('extra.noPreviousPeriodComparison')
          : t('common.previousPeriod', { value: data?.reportChange ?? 0 }),
      to: `/reports?${filter}`,
      get link() {
        return t('extra.viewReports');
      },
    },
    {
      get label() {
        return t('auth.totalClients');
      },
      count: data?.totalClients,
      get unit() {
        return t('workspace.clients');
      },
      Icon: UsersIcon,
      detail:
        data?.clientChange === null
          ? t('extra.noPreviousPeriodComparison')
          : t('common.previousPeriod', { value: data?.clientChange ?? 0 }),
      to: `/clients?reportType=${reportType || 'ALL'}&days=${days}`,
      get link() {
        return t('extra.viewClients');
      },
    },
    {
      get label() {
        return t('auth.reportsInProgress');
      },
      count: data?.processing,
      get unit() {
        return t('workspace.reports');
      },
      Icon: LoaderCircleIcon,
      get detail() {
        return t('extra.youLlBeNotified');
      },
      to: `/reports?${filter}&status=PROCESSING`,
      get link() {
        return t('extra.viewReports');
      },
    },
    {
      get label() {
        return t('auth.pendingReports');
      },
      count: data?.pending,
      get unit() {
        return t('workspace.reports');
      },
      Icon: CircleHelpIcon,
      get detail() {
        return t('extra.reviewAndDecide');
      },
      to: `/reports?${filter}&status=PENDING_REVIEW`,
      get link() {
        return t('extra.viewReports');
      },
    },
  ];
  return (
    <main className="raven-page">
      <span className="raven-label">{t('auth.ravenKycDashboard')}</span>
      <h1>
        {t('auth.welcomeBack')} {user.firstName}
      </h1>
      <p className="mt-4 text-lg">{t('auth.monitorClientsKycFlowAtAGlance')}</p>
      <div className="mt-10 mb-6 flex justify-end gap-3">
        <select
          aria-label={t('reports.reportType')}
          className="raven-select min-w-32"
          onChange={(event) => setReportType(event.target.value as typeof reportType)}
          value={reportType}
        >
          <option value="">{t('reports.allTypes')}</option>
          <option>{t('reports.kyc')}</option>
          <option>{t('reports.kyb')}</option>
        </select>
        <select
          aria-label={t('auth.dashboardPeriod')}
          className="raven-select min-w-36"
          onChange={(event) => setDays(Number(event.target.value))}
          value={days}
        >
          <option value={7}>{t('reports.thisWeek')}</option>
          <option value={30}>{t('reports.last30Days')}</option>
          <option value={180}>{t('reports.last6Months')}</option>
          <option value={365}>{t('reports.lastYear')}</option>
        </select>
      </div>
      <QueryState
        error={query.isError}
        loading={query.isLoading}
        retry={() => void query.refetch()}
      />
      {data && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            {metrics.map(({ label, count, unit, Icon, detail, to, link }) => (
              <div className="raven-stat" key={label}>
                <div className="flex justify-between gap-2 text-xs">
                  {label}
                  <Icon className="size-4" />
                </div>
                <p className="my-3 text-[#172570] text-xl">
                  <strong>{number(count ?? 0)}</strong> {unit}
                </p>
                <span className="raven-muted">{detail}</span>
                <OLink className="raven-link mt-auto self-end pt-7" to={to}>
                  {link} ›
                </OLink>
              </div>
            ))}
            <div className="raven-stat !bg-[#eeedf6] border border-[#cecae6]">
              <h2>{t('auth.createANewReport')}</h2>
              <p className="mt-3 text-sm">{t('auth.beginReviewingANewClient')}</p>
              <OLink
                className="mt-auto flex items-center justify-center gap-2 rounded bg-[#222e9c] p-3 text-sm text-white"
                to="/reports/create"
              >
                <PlusIcon className="size-5" />
                {t('auth.createReport')}{' '}
              </OLink>
            </div>
          </div>
          {data.incomplete > 0 && (
            <p className="mt-5 text-sm">
              {data.incomplete} {t('auth.reportSNeedResearchAttention')}{' '}
              <OLink to={`/reports?${filter}&status=INCOMPLETE`}>
                {t('auth.reviewIncompleteReports')}
              </OLink>
            </p>
          )}
          <div className="mt-7 grid gap-5 lg:grid-cols-2">
            <div className="raven-panel">
              <h2>{t('auth.clientsRiskOverview')}</h2>
              <p className="text-sm">
                {dateLabel(data.start)} – {dateLabel(data.end)}
              </p>
              <MonthlyChart
                rows={data.clientSeries}
                series={['HIGH', 'MEDIUM', 'LOW', 'UNASSESSED']}
              />
            </div>
            <div className="raven-panel">
              <h2>{t('auth.reportsStatusOverview')}</h2>
              <p className="text-sm">
                {dateLabel(data.start)} – {dateLabel(data.end)}
              </p>
              <MonthlyChart rows={data.reportSeries} series={['REJECTED', 'ACCEPTED', 'PENDING']} />
            </div>
          </div>
        </>
      )}
    </main>
  );
};
